<template>
  <div
    class="MobileNav"
  >
    <nav
      id="nav"
      class="mobile-nav"
      @click="scrollToTop()"
    >
      <FeedButton
        v-if="currentUser || !privateMode"
        @click.stop
      />
      <router-link
        v-if="currentUser || !privateMode"
        class="button-unstyled mobile-nav-button"
        active-class="-active"
        :to="{ name: 'search' }"
        :title="$t('nav.search')"
        @click.stop
      >
        <FAIcon
          class="fa-scale-110 fa-old-padding"
          icon="search"
        />
      </router-link>
      <router-link
        v-if="currentUser && pleromaChatMessagesAvailable"
        class="button-unstyled mobile-nav-button"
        :class="{ '-active': $route.name === 'chats' || $route.name === 'chat' }"
        :to="{ name: 'chats', params: { username: currentUser.screen_name } }"
        :title="$t('nav.chats')"
        @click.stop
      >
        <FAIcon
          class="fa-scale-110 fa-old-padding"
          icon="comments"
        />
        <div
          v-if="unreadChatsCount"
          class="badge -dot -notification"
        />
      </router-link>
      <button
        v-if="currentUser"
        class="button-unstyled mobile-nav-button"
        :title="unseenNotificationsCount ? $t('nav.mobile_notifications_unread_active') : $t('nav.mobile_notifications')"
        @click.stop.prevent="openMobileNotifications()"
      >
        <FAIcon
          class="fa-scale-110 fa-old-padding"
          icon="bell"
        />
        <div
          v-if="unseenNotificationsCount"
          class="badge -dot -notification"
        />
      </button>
      <button
        class="button-unstyled mobile-nav-button account-button"
        :title="$t('nav.account')"
        :aria-label="$t('nav.account')"
        :aria-expanded="drawerOpen ? 'true' : 'false'"
        @click.stop.prevent="toggleMobileSidebar()"
      >
        <UserAvatar
          v-if="currentUser"
          :user-id="currentUser.id"
          compact
        />
        <FAIcon
          v-else
          class="fa-scale-110 fa-old-padding"
          icon="user"
        />
        <div
          v-if="followRequestsCount || unreadAnnouncementsCount"
          class="badge -dot -notification"
        />
      </button>
    </nav>
    <aside
      v-if="currentUser"
      class="mobile-notifications-drawer mobile-drawer"
      :class="{ '-closed': !notificationsOpen }"
      @touchstart.stop="notificationsTouchStart"
      @touchmove.stop="notificationsTouchMove"
    >
      <div class="panel-heading mobile-notifications-header">
        <h1 class="title">
          {{ $t('notifications.notifications') }}
          <span
            v-if="unseenCountBadgeText"
            class="badge -notification unseen-count"
          >{{ unseenCountBadgeText }}</span>
        </h1>
        <span class="spacer" />
        <button
          v-if="notificationsAtTop"
          class="button-unstyled mobile-nav-button"
          :title="$t('general.scroll_to_top')"
          @click.stop.prevent="scrollMobileNotificationsToTop"
        >
          <FAIcon
            class="fa-scale-110 fa-old-padding"
            icon="arrow-line-up"
            fixed-width
          />
        </button>
        <button
          v-if="!closingDrawerMarksAsSeen"
          class="button-unstyled mobile-nav-button"
          :title="$t('nav.mobile_notifications_mark_as_seen')"
          @click.stop.prevent="markNotificationsAsSeen()"
        >
          <FAIcon
            class="fa-scale-110 fa-old-padding"
            icon="check-double"
          />
        </button>
        <button
          class="button-unstyled mobile-nav-button"
          :title="$t('nav.mobile_notifications_close')"
          @click.stop.prevent="closeMobileNotifications(true)"
        >
          <FAIcon
            class="fa-scale-110 fa-old-padding"
            icon="times"
          />
        </button>
      </div>
      <!-- Notifications teleport target -->
      <div
        id="mobile-notifications"
        ref="mobileNotifications"
        class="mobile-notifications"
        @scroll="onScroll"
      />
    </aside>
    <SideDrawer
      ref="sideDrawer"
      @toggled="(open) => drawerOpen = open"
    />
  </div>
</template>

<script src="./mobile_nav.js"></script>

<style lang="scss">
.MobileNav {
  z-index: var(--ZI_navbar);

  .mobile-nav {
    display: flex;
    height: var(--navbar-height);
    line-height: var(--navbar-height);
    width: 100%;
    box-sizing: border-box;

    > * {
      flex: 1 1 0;
      min-width: 0;
    }

    /* equal shares: padding would come on top of them */
    > .mobile-nav-button {
      padding-inline: 0;
    }

    /* a list or hashtag: its name next to the icon */
    > .FeedButton.-named {
      flex-grow: 3;
    }

    /* where you are: underlined, in the link colour */
    .-active,
    .FeedButton.-current .popover-trigger-button {
      color: var(--link);
      border-bottom: 4px solid;
      padding-top: 4px;
    }
  }

  .mobile-nav-button {
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
    padding: 0 1em;
    color: inherit;
    cursor: pointer;
    box-sizing: border-box;
  }

  /* no text line around the image: it would sit on its baseline */
  .account-button .Avatar {
    display: flex;
  }

  .account-button .Avatar,
  .account-button .avatar {
    width: 1.75em;
    height: 1.75em;
  }

  .mobile-notifications-drawer {
    width: 100%;
    height: 100vh;
    overflow-x: hidden;
    position: fixed;
    top: 0;
    left: 0;
    box-shadow: var(--shadow);
    transition-property: transform;
    transition-duration: 0.25s;
    transform: translateX(0);
    z-index: var(--ZI_navbar);
    -webkit-overflow-scrolling: touch;
    background: var(--background);

    &.-closed {
      transform: translateX(100%);
      box-shadow: none;
    }
  }

  .mobile-notifications-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    z-index: calc(var(--ZI_navbar) + 100);
    width: 100%;
    height: var(--navbar-height);
    line-height: var(--navbar-height);
    position: absolute;
    box-shadow: var(--shadow);

    .spacer {
      flex: 1;
    }

    .title {
      font-size: var(--fs-xl);
      margin-left: 0.6em;
      white-space: nowrap;
      overflow-x: hidden;
      text-overflow: ellipsis;
    }
  }

  .mobile-notifications {
    margin-top: var(--navbar-height);
    width: 100vw;
    height: calc(100vh - var(--navbar-height));
    overflow: hidden scroll;

    .notifications {
      padding: 0;
      border-radius: 0;
      box-shadow: none;

      .panel {
        border-radius: 0;
        margin: 0;
        box-shadow: none;
      }

      .panel::after {
        border-radius: 0;
      }

      .panel .panel-heading {
        border-radius: 0;
        box-shadow: none;
      }
    }
  }

  .confirm-modal.dark-overlay {
    &::before {
      z-index: var(--ZI_mobile_nav);
    }

    .dialog-modal.panel {
      z-index: calc(var(--ZI_mobile_nav) + 1);
    }
  }
}

</style>
